/**
 * RitualActivityCard - Individual activity card with education and completion
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, Sparkles, Brain, 
  Wind, Heart, Sun, Target, Droplets, ListChecks,
  Cloud, BookOpen, Moon, Smartphone, Scan,
  Info, CheckCircle2, Lightbulb
} from 'lucide-react';
import { RitualActivity } from './types';
import { cn } from '@/lib/utils';

interface RitualActivityCardProps {
  activity: RitualActivity;
  isCompleted: boolean;
  onToggleComplete: (activityId: string) => void;
  showEducation?: boolean;
  isInRoutine?: boolean;
  onAddToRoutine?: () => void;
  onRemoveFromRoutine?: () => void;
}

const iconMap: Record<string, React.ElementType> = {
  wind: Wind,
  heart: Heart,
  sun: Sun,
  target: Target,
  droplets: Droplets,
  'list-checks': ListChecks,
  cloud: Cloud,
  'book-open': BookOpen,
  moon: Moon,
  'smartphone-off': Smartphone,
  scan: Scan,
  sparkles: Sparkles,
};

export const RitualActivityCard: React.FC<RitualActivityCardProps> = ({
  activity,
  isCompleted,
  onToggleComplete,
  showEducation = true,
  isInRoutine = true,
  onAddToRoutine,
  onRemoveFromRoutine,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = iconMap[activity.icon_name] || Sparkles;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card 
        className={cn(
          "p-4 transition-all duration-300 border",
          isCompleted 
            ? "bg-primary/5 border-primary/30" 
            : "bg-card hover:bg-accent/5 border-border/50"
        )}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div 
            className={cn(
              "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
              isCompleted 
                ? "bg-primary/20 text-primary" 
                : "bg-muted text-muted-foreground"
            )}
          >
            <Icon className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={cn(
                "font-medium text-sm",
                isCompleted && "text-primary"
              )}>
                {activity.name}
              </h4>
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                <Clock className="w-3 h-3 mr-1" />
                {activity.duration_minutes}m
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {activity.description}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {showEducation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setIsExpanded(true)}
              >
                <Info className="w-4 h-4" />
              </Button>
            )}
            
            {isInRoutine ? (
              <div 
                className="cursor-pointer"
                onClick={() => onToggleComplete(activity.id)}
              >
                <Checkbox 
                  checked={isCompleted}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all",
                    isCompleted && "bg-primary border-primary"
                  )}
                />
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddToRoutine}
              >
                Add
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Education Drawer */}
      <Drawer open={isExpanded} onOpenChange={setIsExpanded}>
        <DrawerContent className="max-h-[85vh] bg-gradient-to-b from-background via-background to-primary/5">
          {/* Hero Header */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary/10 rounded-full blur-2xl" />
            
            <DrawerHeader className="relative z-10 pb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center shadow-lg border border-primary/20 animate-scale-in">
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <DrawerTitle className="text-xl font-bold text-foreground">
                    {activity.name}
                  </DrawerTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-primary/20 text-primary border-0 px-3 py-1">
                      <Clock className="w-3.5 h-3.5 mr-1.5" />
                      {activity.duration_minutes} minutes
                    </Badge>
                  </div>
                </div>
              </div>
            </DrawerHeader>
          </div>
          
          <ScrollArea className="flex-1 px-4 pb-8">
            <div className="space-y-5 animate-fade-in">
              {/* Description */}
              <p className="text-muted-foreground leading-relaxed">
                {activity.description}
              </p>

              {/* Why This Matters - Featured Card */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary/40 via-secondary/20 to-secondary/5 p-5 border border-secondary/30">
                <div className="absolute top-0 right-0 w-20 h-20 bg-secondary/20 rounded-full blur-2xl" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary/50 to-secondary/20 flex items-center justify-center shadow-sm">
                      <Lightbulb className="w-5 h-5 text-secondary" />
                    </div>
                    <h3 className="font-bold text-foreground text-base">Why This Matters</h3>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {activity.science_explanation}
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center shadow-sm">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground text-base">Key Benefits</h3>
                </div>
                <div className="grid gap-2">
                  {activity.benefits.map((benefit, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 p-3.5 bg-gradient-to-r from-primary/10 to-transparent rounded-xl border border-primary/10 animate-fade-in"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* The Science */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent/20 via-accent/10 to-transparent p-5 border border-accent/20">
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-accent/15 rounded-full blur-xl" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/40 to-accent/15 flex items-center justify-center shadow-sm">
                      <Brain className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="font-bold text-foreground text-base">The Science</h3>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {activity.science_explanation}
                  </p>
                </div>
              </div>

              {/* How to Practice */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center shadow-sm">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground text-base">How to Practise</h3>
                </div>
                <div className="bg-gradient-to-br from-muted/50 to-muted/20 rounded-2xl p-4 border border-border/50 space-y-3">
                  {activity.instructions.split('\n').map((instruction, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-3 animate-fade-in"
                      style={{ animationDelay: `${(index + 3) * 100}ms` }}
                    >
                      <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <p className="text-sm text-foreground/80 leading-relaxed flex-1">
                        {instruction.replace(/^\d+\.\s*/, '')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <Button 
                className="w-full h-14 text-base font-semibold rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-[1.02]" 
                size="lg"
                onClick={() => {
                  onToggleComplete(activity.id);
                  setIsExpanded(false);
                }}
              >
                {isCompleted ? 'Mark as Incomplete' : 'Complete This Activity'}
              </Button>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </motion.div>
  );
};
